import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import "./order-popup.css";
import { TbEdit, TbFileUpload } from "react-icons/tb";

const OrderPopup = ({ onOptionSelect }) => {
  const [opened, setOpened] = useState(true);
  const [selectedOption, setSelectedOption] = useState(null);

  // Use an existing portal container or create one
  const getPortalContainer = () => {
    let container = document.getElementById("order-popup-portal");
    if (!container) {
      container = document.createElement("div");
      container.id = "order-popup-portal";
      document.body.appendChild(container);
    }
    return container;
  };

  const portalContainer = getPortalContainer();

  useEffect(() => {
    document.body.classList.add("modal-open");

    // Cleanup on unmount
    return () => {
      document.body.classList.remove("modal-open");
      // Only remove container if no other components need it
      if (portalContainer && !document.querySelector(".order-popup-overlay")) {
        portalContainer.remove();
      }
    };
  }, [portalContainer]);

  useEffect(() => {
    if (opened) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
  }, [opened]);

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
  };

  const handleCancel = () => {
    setOpened(false);
  };

  const handleContinue = () => {
    if (selectedOption === "fill") {
      console.log("User selected to fill the form");
      onOptionSelect("fill");
    } else if (selectedOption === "upload") {
      console.log("User selected to upload/download the form");
      onOptionSelect("upload");
    }
    setOpened(false);
  };

  if (!opened) return null;

  const popupContent = (
    <div className="order-popup-overlay">
      <div className="order-popup-modal">
        <div className="order-popup-content">
          <h2 className="order-popup-title">
            How do you want to order this test?
          </h2>

          <div className="order-popup-options">
            <div
              className={`order-popup-option ${
                selectedOption === "fill" ? "selected" : ""
              }`}
              onClick={() => handleOptionSelect("fill")}
            >
              <div className="option-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    opacity="0.4"
                    d="M15.48 3H7.52C4.07 3 2 5.06 2 8.52V16.47C2 19.94 4.07 22 7.52 22H15.47C18.93 22 20.99 19.94 20.99 16.48V8.52C21 5.06 18.93 3 15.48 3Z"
                    fill="#3A3F6A"
                  />
                  <path
                    d="M21.0234 2.98223C19.2334 1.18223 17.4834 1.14223 15.6434 2.98223L14.5134 4.10223C14.4134 4.20223 14.3834 4.34223 14.4234 4.47223C15.1234 6.92223 17.0834 8.88223 19.5334 9.58223C19.5634 9.59223 19.6134 9.59223 19.6434 9.59223C19.7434 9.59223 19.8434 9.55223 19.9134 9.48223L21.0234 8.36223C21.9334 7.45223 22.3834 6.58223 22.3834 5.69223C22.3834 4.79223 21.9334 3.90223 21.0234 2.98223Z"
                    fill="#3A3F6A"
                  />
                  <path
                    d="M17.8611 10.4237C17.5911 10.2937 17.3311 10.1638 17.0911 10.0138C16.8911 9.89375 16.6911 9.76375 16.5011 9.62375C16.3411 9.52375 16.1611 9.37375 15.9811 9.22375C15.9611 9.21375 15.9011 9.16375 15.8211 9.08375C15.5111 8.83375 15.1811 8.49375 14.8711 8.12375C14.8511 8.10375 14.7911 8.04375 14.7411 7.95375C14.6411 7.84375 14.4911 7.65375 14.3611 7.44375C14.2511 7.30375 14.1211 7.10375 14.0011 6.89375C13.8511 6.64375 13.7211 6.39375 13.6011 6.13375C13.4711 5.85375 13.3711 5.59375 13.2811 5.34375L7.90108 10.7238C7.55108 11.0738 7.21108 11.7338 7.14108 12.2238L6.71108 15.2037C6.62108 15.8337 6.79108 16.4237 7.18108 16.8137C7.51108 17.1437 7.96108 17.3137 8.46108 17.3137C8.57108 17.3137 8.68108 17.3038 8.79108 17.2938L11.7611 16.8738C12.2511 16.8038 12.9111 16.4738 13.2611 16.1138L18.6411 10.7337C18.3911 10.6537 18.1411 10.5437 17.8611 10.4237Z"
                    fill="#3A3F6A"
                  />
                </svg>
              </div>
              <div className="option-text">Fill requisition form</div>
            </div>

            <div
              className={`order-popup-option ${
                selectedOption === "upload" ? "selected" : ""
              }`}
              onClick={() => handleOptionSelect("upload")}
            >
              <div className="option-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    opacity="0.4"
                    d="M20.5 10.19H17.61C15.24 10.19 13.31 8.26 13.31 5.89V3C13.31 2.45 12.86 2 12.31 2H8.07C4.99 2 2.5 4 2.5 7.57V16.43C2.5 20 4.99 22 8.07 22H15.93C19.01 22 21.5 20 21.5 16.43V11.19C21.5 10.64 21.05 10.19 20.5 10.19Z"
                    fill="#3A3F6A"
                  />
                  <path
                    d="M15.7997 2.21048C15.3897 1.80048 14.6797 2.08048 14.6797 2.65048V6.14048C14.6797 7.60048 15.9197 8.81048 17.4297 8.81048C18.3797 8.82048 19.6997 8.82048 20.8297 8.82048C21.3997 8.82048 21.6997 8.15048 21.2997 7.75048C19.8597 6.30048 17.2797 3.69048 15.7997 2.21048Z"
                    fill="#3A3F6A"
                  />
                  <path
                    d="M11.5275 12.47L9.5275 10.47C9.5175 10.46 9.5075 10.46 9.5075 10.45C9.4475 10.39 9.3675 10.34 9.2875 10.3C9.2775 10.3 9.2775 10.3 9.2675 10.3C9.1875 10.27 9.1075 10.26 9.0275 10.25C8.9975 10.25 8.9775 10.25 8.9475 10.25C8.8875 10.25 8.8175 10.27 8.7575 10.29C8.7275 10.3 8.7075 10.31 8.6875 10.32C8.6075 10.36 8.5275 10.4 8.4675 10.47L6.4675 12.47C6.1775 12.76 6.1775 13.24 6.4675 13.53C6.7575 13.82 7.2375 13.82 7.5275 13.53L8.2475 12.81V17C8.2475 17.41 8.5875 17.75 8.9975 17.75C9.4075 17.75 9.7475 17.41 9.7475 17V12.81L10.4675 13.53C10.6175 13.68 10.8075 13.75 10.9975 13.75C11.1875 13.75 11.3775 13.68 11.5275 13.53C11.8175 13.24 11.8175 12.76 11.5275 12.47Z"
                    fill="#3A3F6A"
                  />
                </svg>
              </div>
              <div className="option-text">Upload requisition form</div>
            </div>
          </div>

          <div className="order-popup-buttons">
            <button className="cancel-button" onClick={handleCancel}>
              Cancel
            </button>
            <button
              className={`continue-button ${!selectedOption ? "disabled" : ""}`}
              onClick={handleContinue}
              disabled={!selectedOption}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(popupContent, portalContainer);
};

export default OrderPopup;
